/**
 * Freeze V2 — Embryo Detail Renderer
 *
 * AG Grid detailCellRenderer for the master fert grid: stacks the temp and
 * live embryo tables, plus SubGridInfo for slaughterhouse sites.
 */
import { memo } from 'react';

import type { CustomDetailCellRendererProps } from 'ag-grid-react';

import type {
  FertAndEmbryosResponse,
  FertilizationListFreezeResponse,
} from '@/services/freeze';

import { useLiveFertEmbryos } from '../../hooks/useLiveFertEmbryos';
import { SLAUGHTER_HOUSE_SITES } from '../../utils/constants';
import { AddEmbryoControls } from './AddEmbryoControls';
import { LiveEmbryoGrid } from './LiveEmbryoGrid';
import { SubGridInfo } from './SubGridInfo';
import { TempEmbryoGrid } from './TempEmbryoGrid';

type EmbryoDetailRendererProps = CustomDetailCellRendererProps & {
  embryosByFert: Map<string, FertAndEmbryosResponse>;
  onRefreshEmbryos: () => void;
};

export const EmbryoDetailRenderer = memo(function EmbryoDetailRenderer(
  props: EmbryoDetailRendererProps,
) {
  const fertData = props.data as FertilizationListFreezeResponse;
  const fertId = fertData.fertId;

  // Subscribes to the embryo query cache so this panel refreshes in place after
  // a refetch. The ref-provided map is only a first-render fallback.
  const liveEmbryoData = useLiveFertEmbryos(fertId);

  if (!fertId) return null;

  const embryoData = liveEmbryoData ?? props.embryosByFert.get(fertId);
  const liveEmbryos = embryoData?.liveEmbryos ?? [];
  const tempEmbryos = embryoData?.tempEmbryos ?? [];
  const isExport = fertData.export !== 'No' && !!fertData.export;
  // Each table needs the other's ids so the parent mating row can be
  // deselected only once *no* embryo of this fert remains selected.
  const liveIds = liveEmbryos
    .map((e) => e.id)
    .filter((id): id is string => !!id);
  const tempIds = tempEmbryos
    .map((e) => e.embryoFreezeEntryId)
    .filter((id): id is string => !!id);
  const isSlaughterHouse = SLAUGHTER_HOUSE_SITES.includes(
    fertData.opuSite ?? '',
  );

  return (
    <div className="flex flex-col gap-3 bg-gray-50 p-3">
      <div className="flex flex-col gap-1">
        <AddEmbryoControls
          fertilizationId={fertId}
          cane={fertData.cane ?? null}
          opuSite={fertData.opuSite ?? null}
          freezeMethod={fertData.freezeMethod ?? null}
          tempEmbryos={tempEmbryos}
          onRefresh={props.onRefreshEmbryos}
        />
        {tempEmbryos.length > 0 && (
          <TempEmbryoGrid
            tempEmbryos={tempEmbryos}
            siblingLiveIds={liveIds}
            isExport={isExport}
            fertilizationId={fertId}
            billAccountCaningOption={fertData.billAccountCaningOption ?? null}
            onRefresh={props.onRefreshEmbryos}
          />
        )}
      </div>

      {liveEmbryos.length > 0 && (
        <div className={isSlaughterHouse ? 'flex gap-4' : ''}>
          {isSlaughterHouse && (
            <SubGridInfo liveEmbryos={liveEmbryos} tempEmbryos={tempEmbryos} />
          )}
          <div className="flex-1">
            <LiveEmbryoGrid
              liveEmbryos={liveEmbryos}
              siblingTempIds={tempIds}
              isExport={isExport}
              fertilizationId={fertId}
              billAccountCaningOption={fertData.billAccountCaningOption ?? null}
              onRefresh={props.onRefreshEmbryos}
            />
          </div>
        </div>
      )}
    </div>
  );
});
